import React, { useState, useEffect } from 'react';
import './NavbarDashboard.css';
import { Link as Anchor, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartPie,
    faUser,
    faBook,
    faImage,
    faAddressBook,
    faTags,
    faTicketAlt,
    faTable,
    faClipboardList,
    faGear,
    faUtensils,
    faCashRegister,
    faBell,
    faReceipt,
    faThumbtack,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../../images/logo.png';
import baseURL from '../../url';
import { getConfig, fetchConfigFromDB, applyTheme, DEFAULT_THEME_COLOR } from '../../../utils/configService';

export default function NavbarDashboard() {
    const location = useLocation();
    const [rol, setRol] = useState('admin');
    const [nombreNegocio, setNombreNegocio] = useState('Restaurante & Tienda');
    const [themeColor, setThemeColor] = useState(() => {
        const cfg = getConfig();
        return cfg?.themeColor || DEFAULT_THEME_COLOR;
    });
    const [isPinned, setIsPinned] = useState(() => {
        try {
            return localStorage.getItem('sidebar_pinned') === 'true';
        } catch (e) {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        // Cargar rol desde localStorage si existe previamente
        try {
            const storedUser = localStorage.getItem('usuario');
            if (storedUser) {
                const parsed = JSON.parse(storedUser);
                if (parsed && parsed.rol) {
                    setRol(parsed.rol.toLowerCase().trim());
                }
            }
        } catch (e) {}

        // Cargar rol del usuario desde endpoint
        fetch(`${baseURL}/userLogued.php`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (data && data.rol) {
                    setRol(data.rol.toLowerCase().trim());
                }
            })
            .catch(() => {});

        // Cargar configuración de negocio y tema desde caché local y DB
        const cfg = getConfig();
        if (cfg) {
            if (cfg.nombreNegocio) setNombreNegocio(cfg.nombreNegocio);
            if (cfg.themeColor) {
                setThemeColor(cfg.themeColor);
                applyTheme(cfg.themeColor);
            }
        }

        fetchConfigFromDB().then((dbConf) => {
            if (dbConf) {
                if (dbConf.nombreNegocio) setNombreNegocio(dbConf.nombreNegocio);
                if (dbConf.themeColor) {
                    setThemeColor(dbConf.themeColor);
                    applyTheme(dbConf.themeColor);
                }
            }
        });

        const handleConfigChange = (e) => {
            if (e?.detail?.nombreNegocio) {
                setNombreNegocio(e.detail.nombreNegocio);
            }
            if (e?.detail?.themeColor) {
                setThemeColor(e.detail.themeColor);
                applyTheme(e.detail.themeColor);
            }
        };

        const handleToggleMobile = () => {
            setMobileOpen(prev => !prev);
        };

        const handleCloseMobile = () => {
            setMobileOpen(false);
        };

        window.addEventListener('configUpdated', handleConfigChange);
        window.addEventListener('toggleSidebarMobile', handleToggleMobile);
        window.addEventListener('closeSidebarMobile', handleCloseMobile);

        return () => {
            window.removeEventListener('configUpdated', handleConfigChange);
            window.removeEventListener('toggleSidebarMobile', handleToggleMobile);
            window.removeEventListener('closeSidebarMobile', handleCloseMobile);
        };
    }, []);

    const togglePin = () => {
        setIsPinned(prev => {
            const next = !prev;
            try {
                localStorage.setItem('sidebar_pinned', String(next));
                window.dispatchEvent(new CustomEvent('sidebarPinChanged', { detail: { pinned: next } }));
            } catch (e) {}
            return next;
        });
    };

    const cerrarMobile = () => {
        setMobileOpen(false);
    };

    const cleanRol = (rol || 'admin').toString().toLowerCase().trim();
    const esAdmin = cleanRol === 'admin' || cleanRol === 'administrador' || cleanRol === '';
    const esCocinero = cleanRol === 'cocinero';
    const esCajero = cleanRol === 'cajero';
    const esMesero = cleanRol === 'mesero';

    return (
        <>
            {/* Overlay para móvil */}
            {mobileOpen && (
                <div className="sidebarMobileOverlay" onClick={cerrarMobile}></div>
            )}

            <aside
                className={`navbarDashboard ${isPinned ? 'is-pinned' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
                style={{ backgroundColor: themeColor }}
            >
                {/* Cabecera del sidebar: Logo + Nombre de Negocio + Botón Fijar */}
                <div className="sidebarHeader">
                    <div className="sidebarBrand">
                        <img src={logo} alt="Logo" className="sidebarLogoImg" />
                        <span className="sidebarBusinessName" title={nombreNegocio}>
                            {nombreNegocio}
                        </span>
                    </div>

                    <div className="sidebarHeaderControls">
                        <button
                            type="button"
                            className={`btnPinSidebar ${isPinned ? 'pinned' : ''}`}
                            onClick={togglePin}
                            title={isPinned ? "Desbloquear menú (ocultar al salir)" : "Fijar menú lateral"}
                        >
                            <FontAwesomeIcon icon={faThumbtack} />
                        </button>

                        <button
                            type="button"
                            className="btnCloseMobileSidebar"
                            onClick={cerrarMobile}
                            title="Cerrar menú"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Navegación y Enlaces (Alineados a la izquierda) */}
                <nav className="sidebarLinks">
                    {/* Inicio / Dashboard KPI (Admin) */}
                    {esAdmin && (
                        <Anchor
                            to="/dashboard"
                            className={`sidebarLink ${location.pathname === '/dashboard' ? 'activeLink' : ''}`}
                            title="Dashboard"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faChartPie} className="sidebarIcon" />
                            <span className="sidebarText">Dashboard</span>
                        </Anchor>
                    )}

                    {/* Punto de Venta POS (Admin y Cajero) */}
                    {(esAdmin || esCajero) && (
                        <Anchor
                            to="/dashboard/pos"
                            className={`sidebarLink ${location.pathname === '/dashboard/pos' ? 'activeLink' : ''}`}
                            title="Punto de Venta POS"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faCashRegister} className="sidebarIcon" />
                            <span className="sidebarText">POS</span>
                        </Anchor>
                    )}

                    {/* Panel de Cocina KDS (Admin y Cocinero) */}
                    {(esAdmin || esCocinero) && (
                        <Anchor
                            to="/dashboard/kds"
                            className={`sidebarLink ${location.pathname === '/dashboard/kds' ? 'activeLink' : ''}`}
                            title="Cocina KDS"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faUtensils} className="sidebarIcon" />
                            <span className="sidebarText">Cocina KDS</span>
                        </Anchor>
                    )}

                    {/* Panel de Meseros (Admin y Mesero) */}
                    {(esAdmin || esMesero) && (
                        <Anchor
                            to="/dashboard/meseros"
                            className={`sidebarLink ${location.pathname === '/dashboard/meseros' ? 'activeLink' : ''}`}
                            title="Panel Meseros"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faBell} className="sidebarIcon" />
                            <span className="sidebarText">Meseros</span>
                        </Anchor>
                    )}

                    {/* Mesas (Admin, Mesero, Cajero) */}
                    {(esAdmin || esMesero || esCajero) && (
                        <Anchor
                            to="/dashboard/mesas"
                            className={`sidebarLink ${location.pathname === '/dashboard/mesas' ? 'activeLink' : ''}`}
                            title="Mesas y QR"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faTable} className="sidebarIcon" />
                            <span className="sidebarText">Mesas</span>
                        </Anchor>
                    )}

                    {/* Pedidos (Admin, Cajero, Mesero) */}
                    {(esAdmin || esCajero || esMesero) && (
                        <Anchor
                            to="/dashboard/pedidos"
                            className={`sidebarLink ${location.pathname === '/dashboard/pedidos' ? 'activeLink' : ''}`}
                            title="Pedidos"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faClipboardList} className="sidebarIcon" />
                            <span className="sidebarText">Pedidos</span>
                        </Anchor>
                    )}

                    {/* Gastos (Admin y Cajero) */}
                    {(esAdmin || esCajero) && (
                        <Anchor
                            to="/dashboard/gastos"
                            className={`sidebarLink ${location.pathname === '/dashboard/gastos' ? 'activeLink' : ''}`}
                            title="Gastos"
                            onClick={cerrarMobile}
                        >
                            <FontAwesomeIcon icon={faReceipt} className="sidebarIcon" />
                            <span className="sidebarText">Gastos</span>
                        </Anchor>
                    )}

                    {/* Módulos de administración exclusiva */}
                    {esAdmin && (
                        <>
                            <div className="sidebarDivider"></div>

                            <Anchor
                                to="/dashboard/productos"
                                className={`sidebarLink ${location.pathname === '/dashboard/productos' ? 'activeLink' : ''}`}
                                title="Productos"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faBook} className="sidebarIcon" />
                                <span className="sidebarText">Productos</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/categorias"
                                className={`sidebarLink ${location.pathname === '/dashboard/categorias' ? 'activeLink' : ''}`}
                                title="Categorías"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faTags} className="sidebarIcon" />
                                <span className="sidebarText">Categorías</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/cupones"
                                className={`sidebarLink ${location.pathname.startsWith('/dashboard/cupon') || location.pathname.startsWith('/dashboard/codigo') ? 'activeLink' : ''}`}
                                title="Cupones de Descuento"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faTicketAlt} className="sidebarIcon" />
                                <span className="sidebarText">Cupones</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/banners"
                                className={`sidebarLink ${location.pathname === '/dashboard/banners' ? 'activeLink' : ''}`}
                                title="Banners"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faImage} className="sidebarIcon" />
                                <span className="sidebarText">Banners</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/contacto"
                                className={`sidebarLink ${location.pathname === '/dashboard/contacto' ? 'activeLink' : ''}`}
                                title="Contactos"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faAddressBook} className="sidebarIcon" />
                                <span className="sidebarText">Contactos</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/usuarios"
                                className={`sidebarLink ${location.pathname === '/dashboard/usuarios' ? 'activeLink' : ''}`}
                                title="Usuarios y Roles"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faUser} className="sidebarIcon" />
                                <span className="sidebarText">Usuarios</span>
                            </Anchor>

                            <Anchor
                                to="/dashboard/configuracion"
                                className={`sidebarLink ${location.pathname === '/dashboard/configuracion' ? 'activeLink' : ''}`}
                                title="Configuraciones"
                                onClick={cerrarMobile}
                            >
                                <FontAwesomeIcon icon={faGear} className="sidebarIcon" />
                                <span className="sidebarText">Configuración</span>
                            </Anchor>
                        </>
                    )}
                </nav>
            </aside>
        </>
    );
}
